import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';

/**
 * Render an icon by class string. Supports both PrimeIcons ("pi pi-*") via an
 * <i> element and FontAwesome solid icons ("fa-solid fa-building" / "fa-building")
 * via the <FontAwesomeIcon> component.
 *
 * No global FontAwesome webfont CSS is loaded, so a raw <i class="fa-*"> would
 * not render — FA icons MUST go through <FontAwesomeIcon>. This component picks
 * the right renderer so callers can pass either family.
 */
export interface AppIconProps {
  /** Icon class, e.g. "pi pi-building" or "fa-solid fa-building". */
  icon: string;
  /** Optional extra class names applied to the rendered element. */
  className?: string;
  /** Optional inline color. */
  color?: string;
}

const AppIcon: React.FC<AppIconProps> = ({ icon, className, color }) => {
  if (icon.includes('fa-')) {
    const name = icon
      .split(/\s+/)
      .map((t) => t.trim())
      .filter((t) => t.startsWith('fa-'))
      .map((t) => t.replace(/^fa-/, ''))
      .filter((t) => !['solid', 'regular', 'brands', 'light'].includes(t))
      .pop();
    const def = name
      ? fas[
          `fa${name
            .split('-')
            .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
            .join('')}` as keyof typeof fas
        ]
      : undefined;
    if (def) {
      return (
        <FontAwesomeIcon icon={def} className={className} style={{ color }} />
      );
    }
  }
  return (
    <i
      className={className ? `${className} ${icon}` : icon}
      style={{ color }}
    />
  );
};

export default AppIcon;
